import type { LegMethod, TransportType } from '../services/api';

/**
 * Spec 053 Phase 3 (S10) — per-leg delivery methods.
 *
 * An order buys pickup and return independently: each leg is either GARAGAT (a
 * Garagat driver does the trip) or SELF (the customer drops the car off / collects
 * it at the garage). The legacy whole-order `deliveryMethod` is kept by the backend
 * as a derived mirror (delta D3/D7), so responses carry both and we must accept
 * either shape — orders created before the per-leg columns landed only have the
 * mirror.
 *
 * Back-compat mapping (delta D4, inverse): DELIVERY → (GARAGAT, GARAGAT),
 * SELF_SERVICE → (SELF, SELF).
 */

/** Copy required by the Phase 3 mobile/partner contract — keep these strings verbatim. */
export const SELF_PICKUP_COPY = 'Drop off at the garage';
export const SELF_RETURN_COPY = 'Collect from the garage';

/** The subset of the order response these helpers need — satisfied by both
 *  `OrderListItem` and `OrderDetail`. */
export interface OrderLegSource {
  status: string;
  deliveryMethod?: 'DELIVERY' | 'SELF_SERVICE' | null;
  pickupMethod?: LegMethod | null;
  returnMethod?: LegMethod | null;
}

export interface OrderLegs {
  pickup: LegMethod;
  return: LegMethod;
}

export function resolveLegMethods(order: OrderLegSource): OrderLegs {
  const legacy: LegMethod = order.deliveryMethod === 'DELIVERY' ? 'GARAGAT' : 'SELF';
  return {
    pickup: order.pickupMethod ?? legacy,
    return: order.returnMethod ?? legacy,
  };
}

/**
 * BR-22 / BR-23 — "Vehicle received" is reachable from PICKUP_ASSIGNED when Garagat
 * drives the pickup leg, and directly from CREATED when the customer drops the car
 * off themselves (delta D8/D10: the transition table carries both edges, the leg
 * predicate decides which one this order may take). Anything else answers 409, so
 * the button must not be offered.
 */
export function canMarkVehicleReceived(order: OrderLegSource): boolean {
  const legs = resolveLegMethods(order);
  if (legs.pickup === 'GARAGAT') return order.status === 'PICKUP_ASSIGNED';
  return order.status === 'CREATED';
}

/**
 * "Collected by customer" is the SELF return leg's counterpart of the drop-off driver
 * states: SERVICE_COMPLETE → COMPLETED. When Garagat drives the return leg the car is
 * handed to a driver, not to the customer, so the garage is never the actor there —
 * that edge (DROPOFF_ASSIGNED → COMPLETED) stays with admin/driver as it is today.
 */
export function canMarkCollectedByCustomer(order: OrderLegSource): boolean {
  const legs = resolveLegMethods(order);
  return legs.return === 'SELF' && order.status === 'SERVICE_COMPLETE';
}

/** Badge label for the "Method" column / header chip. */
export function methodLabel(order: OrderLegSource): string {
  const legs = resolveLegMethods(order);
  if (legs.pickup === 'GARAGAT' && legs.return === 'GARAGAT') return 'Delivery';
  if (legs.pickup === 'GARAGAT') return 'Pickup by Garagat';
  if (legs.return === 'GARAGAT') return 'Return by Garagat';
  return 'Self Service';
}

export function hasGaragatLeg(order: OrderLegSource): boolean {
  const legs = resolveLegMethods(order);
  return legs.pickup === 'GARAGAT' || legs.return === 'GARAGAT';
}

/** The self-leg instruction lines for this order, in leg order. */
export function selfLegHints(order: OrderLegSource): string[] {
  const legs = resolveLegMethods(order);
  const hints: string[] = [];
  if (legs.pickup === 'SELF') hints.push(SELF_PICKUP_COPY);
  if (legs.return === 'SELF') hints.push(SELF_RETURN_COPY);
  return hints;
}

export function pickupLegLabel(order: OrderLegSource): string {
  return resolveLegMethods(order).pickup === 'GARAGAT' ? 'Garagat pickup' : SELF_PICKUP_COPY;
}

export function returnLegLabel(order: OrderLegSource): string {
  return resolveLegMethods(order).return === 'GARAGAT' ? 'Garagat return' : SELF_RETURN_COPY;
}

/**
 * Spec 072 v2 (AC-15) — the garage's words for how the car travels on Garagat's legs. Null for no
 * type (no Garagat leg, or an orders build before v2), so the card shows no chip rather than a guess.
 */
export function transportLabel(type: TransportType | string | null | undefined): 'Zofeur driver' | 'Recovery truck' | null {
  if (type === 'ZOFEUR') return 'Zofeur driver';
  if (type === 'RECOVERY') return 'Recovery truck';
  return null;
}

const LEG_SCHEDULE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Spec 072 v2 (AC-15) — a leg's `scheduledAt` instant as the garage reads it, in UAE time whatever
 * the browser's zone ("24 Oct 2026, 14:30"); '—' when the leg has no time or it does not parse.
 */
export function formatLegScheduled(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return LEG_SCHEDULE_FORMAT.format(at);
}

/** The order fields a leg's "Scheduled" fallback reads — satisfied by `OrderDetail`. */
export interface LegScheduleSource {
  scheduledDate?: string | null;
  pickupSlotLabel?: string | null;
  pickupTimeSlotLabel?: string | null;
  /** #1178 — the return slot chosen at booking. */
  returnTimeSlotLabel?: string | null;
  /** Spec 077 — the return day when an admin moved it; null/absent = the booking day. */
  returnDate?: string | null;
}

/**
 * Spec 072 v2 (AC-15) — what a leg's "Scheduled" row shows. The leg's own `scheduledAt` wins; an
 * admin-assigned leg has none, so the pickup leg falls back to the booking date + pickup slot
 * ("2026-10-29 · Morning (08:00 - 12:00)", as in the order header) and the return leg to the
 * return slot, on `returnDate ?? scheduledDate` (Spec 077). '—' when nothing is known.
 */
export function legScheduledText(
  leg: { type: string; scheduledAt?: string | null },
  order: LegScheduleSource,
): string {
  const own = formatLegScheduled(leg.scheduledAt);
  if (own !== '—') return own;
  if (leg.type === 'PICKUP') {
    const parts = [order.scheduledDate, order.pickupTimeSlotLabel ?? order.pickupSlotLabel].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : '—';
  }
  if (leg.type === 'RETURN_DELIVERY' || leg.type === 'DELIVERY') {
    if (!order.returnTimeSlotLabel) return '—';
    const day = order.returnDate ?? order.scheduledDate;
    return day ? `${day} · ${order.returnTimeSlotLabel}` : order.returnTimeSlotLabel;
  }
  return '—';
}
